import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState
} from "react";
import CrisisResourceModal from "@/components/CrisisResourceModal";
import type { CrisisCooldownBody } from "@/utils/serenity-api-client";

interface CrisisResourcesContextType {
  /** Open the resource-only variant of the modal (no pause copy). */
  openResources: () => void;
  /** Open the pause variant of the modal, populated by a 423 response body. */
  openCooldown: (cooldown: CrisisCooldownBody) => void;
}

const CrisisResourcesContext = createContext<
  CrisisResourcesContextType | undefined
>(undefined);

interface CrisisResourcesProviderProps {
  children: ReactNode;
}

/**
 * Owns the single `CrisisResourceModal` instance for the drawer surface.
 *
 * Two consumers open it: the drawer "Crisis resources" item and the chat
 * footer open the resource-only variant; the chat session-start error
 * handler opens the pause variant with a server-provided resume time.
 */
export function CrisisResourcesProvider({
  children
}: CrisisResourcesProviderProps) {
  const [cooldown, setCooldown] = useState<CrisisCooldownBody | null>(null);
  const [resourcesVisible, setResourcesVisible] = useState(false);

  const openResources = useCallback(() => setResourcesVisible(true), []);
  const openCooldown = useCallback(
    (body: CrisisCooldownBody) => setCooldown(body),
    []
  );

  const handleClose = useCallback(() => {
    setCooldown(null);
    setResourcesVisible(false);
  }, []);

  const value = useMemo(
    () => ({ openResources, openCooldown }),
    [openResources, openCooldown]
  );

  return (
    <CrisisResourcesContext.Provider value={value}>
      {children}
      <CrisisResourceModal
        visible={cooldown !== null || resourcesVisible}
        cooldown={cooldown}
        onClose={handleClose}
      />
    </CrisisResourcesContext.Provider>
  );
}

/**
 * Open the crisis resources modal from any descendant of
 * `CrisisResourcesProvider`.
 */
export function useCrisisResources(): CrisisResourcesContextType {
  const ctx = useContext(CrisisResourcesContext);
  if (ctx === undefined) {
    throw new Error(
      "useCrisisResources must be used inside CrisisResourcesProvider"
    );
  }
  return ctx;
}
